<script>
import { validationMixin } from 'vuelidate'
import { getToolsClient } from '@/services/tools'

const toolsAxios = getToolsClient()

export default {
  components: {},
  mixins: [validationMixin],
  props: {
    isOpen: { type: Boolean, default: false },
    modal: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
    vehicleId: { type: Number, default: null },
    vehicle: {type: Object, default: () => {}},
    region: {
      type: String,
      default: 'SG'
    },
  },
  data() {
    return {
      pinCode: null,
      form: {
        alert_operational_start: null,
        alert_operational_end: null,
      },
    }
  },
  mounted() {
    this.form.alert_operational_start = this.vehicle.alert_operational_start?.slice(0, -3)
    this.form.alert_operational_end = this.vehicle.alert_operational_end?.slice(0, -3)
  },
  validations: {
    form: {
      alert_operational_start: {
        validFormat(val) {
            return !val || /^\d{1,2}:\d{2}$/.test(val);
        },
        required(val, { alert_operational_end }) {
          if (!val && alert_operational_end) return false
          if (val && !alert_operational_end) return true
          return true
        },
        inBetween(val) {
          return !val || (parseFloat(this.normalizeTime(val)) <= 24.0 && parseFloat(this.normalizeTime(val)) >= 0.0)
        },
      },
      alert_operational_end: {
        validFormat(val) {
            return !val || /^\d{1,2}:\d{2}$/.test(val);
        },
        required(val, { alert_operational_start }) {
          if (!val && alert_operational_start) return false
          if (val && !alert_operational_start) return true
          return true
        },
        maxValue(val, { alert_operational_start }) {
          return (
            !val ||
            !alert_operational_start ||
            parseFloat(this.normalizeTime(val)) > parseFloat(this.normalizeTime(alert_operational_start))
          )
        },
        inBetween(val) {
          return !val || (parseFloat(this.normalizeTime(val)) <= 24.0 && parseFloat(this.normalizeTime(val)) >= 0.0)
        },
      },
    },
  },
  computed: {
    showModal: {
      get() {
        return this.modal
      },
      set(newVal) {
        this.$emit('update:modal', newVal)
      },
    },
    loading: {
      get() {
        return this.isLoading
      },
      set(newVal) {
        this.$emit('update:isLoading', newVal)
      },
    },
  },
  methods: {
    normalizeTime(time) {
        return time?.replace(":", ".");
    },
    handleOk(bvModalEvent) {
      bvModalEvent.preventDefault()
    },
    async handleSubmit(bvModalEvent) {
      this.loading = true
      bvModalEvent.preventDefault()
      this.$v.form.$touch()

      if (this.$v.form.$error) {
        this.loading = false
        return
      }

      let payload = {}

      payload.license_plate = this.vehicle.license_plate
      payload.imei = this.vehicle.imei
      payload.company = {
        company_id : this.vehicle.company_id,
        company_name : "mceasy", //workaround
      },
      payload.vehicle_type = {
        vehicle_type_id: this.vehicle.vehicle_type_id,
      },
      payload.alert_operational_start = this.form.alert_operational_start
      payload.alert_operational_end = this.form.alert_operational_end

      const url = `/masterdata/vehicle/${this.vehicleId}`
      const options = {
        method: 'PATCH',
        url: url,
        data: payload,
        headers: {
          'X-Region': this.region,
        },
      }

      const response = await toolsAxios(options).catch((error) => {
        this.handleError(error)
      })

      if (!response?.data) {
        this.loading = false
        return
      }

      this.form.alert_operational_start = response.data.alert_operational_start?.slice(0, -3)
      this.form.alert_operational_end = response.data.alert_operational_end?.slice(0, -3)

      await this.$nextTick()
      this.$toast.success(this.$t('global.success.save'))
      this.loading = false
    },
    handleError(error) {
      if (error.response) {
        this.$toast.error(error.response.data.error)
      } else {
        this.$toast.error(error.message)
      }
      return
    },
    formatRange(event) {
      const charCode = event.keyCode || event.which;
      const charStr = String.fromCharCode(charCode);
      const input = event.target;
      const currentValue = input.value;

      if (!charStr.match(/[0-9:]/)) {
        event.preventDefault();
        return;
      }

      if (charStr === ':' && currentValue.includes(':')) {
        event.preventDefault();
        return;
      }

      const cursorStart = input.selectionStart;
      const cursorEnd = input.selectionEnd;

      const newValue =
        currentValue.slice(0, cursorStart) + charStr + currentValue.slice(cursorEnd);

      if (newValue.length > 5) {
        event.preventDefault();
        return;
      }

      const [hour, minute] = newValue.split(':');

      if (hour && (hour.length > 2 || Number(hour) > 23)) {
        event.preventDefault();
        return;
      }

      if (minute && (minute.length > 2 || Number(minute) > 59)) {
        event.preventDefault();
        return;
      }
    },
    resetForm() {
      this.form = {
        alert_operational_start: null,
        alert_operational_end: null,
      }
      this.originalForm = { ...this.form }
    }
  },
}
</script>
<template>
    <div>
      <b-modal
        v-model="showModal"
        class="mc-modal"
        id="modal-form"
        title="Jam Operasional Kendaraan"
        size="xl"
        centered
        :hide-header-close="loading"
        no-close-on-backdrop
      >
        <div class="card-body mc-modal__filter p-2">
          <b-form @submit.stop.prevent="handleSubmit">
            <b-row>  
              <b-col cols="6">
                <label class="mt-3">{{ $t('sensor.operationalTimeStart') }}</label>
                <b-form-input
                  v-model="form.alert_operational_start"
                  type="text"
                  :placeholder="$t('sensor.operationalTimeStartExample')"
                  :class="{
                    'is-invalid': $v.form.alert_operational_start.$error,
                  }"
                  @keypress="formatRange"
                >
                </b-form-input>
                <div v-if="$v.form.alert_operational_start.$error" class="invalid-feedback">
                  <span v-if="!$v.form.alert_operational_start.required">{{ $t('sensor.required') }}</span>
                  <span v-else-if="!$v.form.alert_operational_start.inBetween">
                    {{ $t('sensor.operationalTimeStart') + ' ' + $t('sensor.inBetween') }}
                  </span>
                  <span v-else-if="!$v.form.alert_operational_start.validFormat">
                    Format harus HH:MM (contoh: 9:00 atau 12:30)
                  </span>
                </div>
              </b-col>
  
              <b-col cols="6">
                <label class="mt-3">{{ $t('sensor.operationalTimeFinish') }}</label>
                <b-form-input
                  v-model="form.alert_operational_end"
                  type="text"
                  :placeholder="$t('sensor.operationalTimeFinishExample')"
                  :class="{
                    'is-invalid': $v.form.alert_operational_end.$error,
                  }"
                  @keypress="formatRange"
                >
                </b-form-input>
                <div v-if="$v.form.alert_operational_end.$error" class="invalid-feedback">
                  <span v-if="!$v.form.alert_operational_end.required">{{ $t('sensor.required') }}</span>
                  <span v-else-if="!$v.form.alert_operational_end.maxValue">
                    {{ $t('sensor.valueMoreThan') + ' ' + $t('sensor.operationalTimeFinish') }}
                  </span>
                  <span v-else-if="!$v.form.alert_operational_end.inBetween">
                    {{ $t('sensor.operationalTimeFinish') + ' ' + $t('sensor.inBetween') }}
                  </span>
                  <span v-else-if="!$v.form.alert_operational_end.validFormat">
                    Format harus HH:MM (contoh: 9:00 atau 12:30)
                  </span>
                </div>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col cols="12">
                <div class="alert alert-info">
                  <i class="fas fa-info-circle mr-2"></i>
                  {{ $t('sensor.operationalTimeInfo') }}
                </div>
              </b-col>
            </b-row>
          </b-form>
        </div>
        <template #modal-footer="{ cancel }">
          <b-row class="mc-modal__filter-footer">
            <b-col cols="12" class="mc-modal__filter-footer-btn">
              <b-button variant="outline-primary" @click="cancel()" :disabled="loading" class="me-3">
                Batal
              </b-button>
              <b-button
                @click="handleSubmit"
                variant="primary"
                :disabled="loading"
              >
                <b-spinner v-if="loading" small type="grow"></b-spinner>
                {{ loading ? 'Loading...' : 'Simpan' }}
              </b-button>
            </b-col>
          </b-row>
        </template>
      </b-modal>
    </div>
  </template>
